/**
 * `lib/publishedDays.ts` — "what is the last day the member can actually
 * see a published roster for, and what is the next one?"
 *
 * Both answers come out of the SAME rows the member's schedule read
 * already returns (`GET /api/account/me/schedule`), whose payload is
 * exactly what that member is allowed to see: the endpoint filters
 * `Assignment.is_published` in SQL and then runs every row through the
 * shared release-region predicate. So "a published day" here means
 * "a day this member has a visible, published row on" — never a claim
 * about a draft the lead is still building.
 *
 * One definition for every caller, so the member app can never disagree
 * with itself about where the published roster ends:
 *
 *   * the request flow's date cap — an MC may be reported for any day
 *     inside the published horizon, even past the ordinary +35d / +183d
 *     planning cap (KKH-REQ-05, hardening §9.3);
 *   * the calendar's empty state — "Next published day: Mon 2 Nov →";
 *   * the MC form's opening day — "Your next published shift · Mon 2 Nov
 *     · 55 · AM", picked before the member taps anything (#1909, 1 Oct).
 *
 * #1909 (30 Sep 2026) — the CAP is a different question from either of
 * those. "The last day I have a seat on" is a fact about one member;
 * "the last day the roster is published to" is a fact about the ROSTER,
 * and it is the second one an MC may be reported up to. A member whose
 * last seat fell on Wednesday of a Mon–Fri published week was offered
 * 1–4 Nov while the colleague seated on Friday was offered 1–6 Nov, on
 * the same published roster. `publishedHorizonIso` answers the roster
 * question from the `published_until` the read now carries, and only
 * falls back to the member's own rows on an older payload.
 */

/** The minimum a row needs for either question. */
export interface PublishedDayRow {
  date: string;
  is_published?: boolean | null;
}

/** As much of `GET /me/schedule` as the horizon question needs. */
export interface PublishedHorizonSource {
  /** The published roster's last day (`published_until`); may be absent
   *  on a payload from an API older than #1909. */
  published_until?: string | null;
  assignments?: readonly PublishedDayRow[] | undefined;
}

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** A row counts only when it is explicitly published. An absent flag
 *  (an older payload, a hand-built fixture) is NOT taken as a yes — the
 *  cap must never widen on a guess. */
function isPublished(row: PublishedDayRow): boolean {
  return row.is_published === true;
}

/** The LAST published day in `rows`, or null when there is none. */
export function lastPublishedDayIso(rows: readonly PublishedDayRow[] | undefined): string | null {
  let last: string | null = null;
  for (const row of rows ?? []) {
    if (!isPublished(row)) continue;
    if (last === null || row.date > last) last = row.date;
  }
  return last;
}

/**
 * The last day of the PUBLISHED ROSTER the member is on — the MC cap.
 *
 * `published_until` (the cycle's window end) is the answer; the
 * member's own last seated day is only a floor under it, kept so an
 * older payload still lifts the cap at all and so the cap can never
 * come out lower than it used to be.
 */
export function publishedHorizonIso(source: PublishedHorizonSource | undefined): string | null {
  const rosterEnd = source?.published_until ?? null;
  const ownLastSeat = lastPublishedDayIso(source?.assignments);
  if (rosterEnd === null) return ownLastSeat;
  if (ownLastSeat === null) return rosterEnd;
  return rosterEnd > ownLastSeat ? rosterEnd : ownLastSeat;
}

/** The FIRST published day strictly after `afterIso`, or null. */
export function nextPublishedDayIso(
  rows: readonly PublishedDayRow[] | undefined,
  afterIso: string,
): string | null {
  let next: string | null = null;
  for (const row of rows ?? []) {
    if (!isPublished(row)) continue;
    if (row.date <= afterIso) continue;
    if (next === null || row.date < next) next = row.date;
  }
  return next;
}

/**
 * The FIRST published day ON OR AFTER `fromIso`, with every published row
 * on it — or null when there is none.
 *
 * Inclusive, unlike `nextPublishedDayIso`: this is "my next shift" for a
 * member reporting sick (#1909, 1 Oct 2026), and a member seated today
 * who wakes up sick is off TODAY. The rows come back as the caller's own
 * type so it can name the seat (`role`) without a second pass that
 * re-decides what "published" means.
 */
export function firstPublishedDayFrom<Row extends PublishedDayRow>(
  rows: readonly Row[] | undefined,
  fromIso: string,
): { date: string; rows: Row[] } | null {
  let first: string | null = null;
  for (const row of rows ?? []) {
    if (!isPublished(row) || row.date < fromIso) continue;
    if (first === null || row.date < first) first = row.date;
  }
  if (first === null) return null;
  const day = first;
  return { date: day, rows: (rows ?? []).filter((row) => isPublished(row) && row.date === day) };
}

/**
 * Today, as the MEMBER APP reckons it — `2026-10-02`: the member's own
 * calendar day, read off the device clock in its own time zone.
 *
 * Roster days are date-only strings, so labels are still formatted with
 * `timeZone: "UTC"` (a date-only string parsed as UTC midnight prints its
 * own day under every zone). "Today" is a different question: it is the
 * day on the member's wall. Every surface that compares a day against it
 * — the month grid's ringed cell, `<MemberAppState/>`'s opening day,
 * `<DayStrip/>`'s marker, the hour grid's now-rule, Who's-on's default
 * day, and the `Report off sick (MC)` form's first selectable day, its
 * marked today and its default — reads it HERE, so no two of them can
 * disagree about which cell is today.
 *
 * Decision 12 (1 Oct 2026 truth pass): this used to be the UTC day
 * (`toISOString()`), so in Singapore before 8 am the grid ringed
 * YESTERDAY as today and the MC form let a member pick it. The server is
 * unchanged.
 */
export function calendarTodayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * `2026-11-02` → `Mon 2 Nov`. Deterministic (UTC + fixed names) rather
 * than `toLocaleDateString`, so the same string renders under every
 * runtime locale — the member app says one date one way.
 */
export function formatShortDayLabel(iso: string): string {
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  return `${WEEKDAY_NAMES[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
}
